import type { useRequest } from '@/composables/useRequest'
import { computed } from 'vue'
import useDocumentList from '@/composables/useDocumentList'
import { useProvideDocumentDataStore } from '@/layout/composables/useDocumentDataStore'
import utils from '@/plugins/utils'

/**
 * Adapts the legacy request document API to the shared document data store.
 * V3 applications can override this provider in their own route root.
 */
export function useProvideRequestDocumentDataStore(
  request: ReturnType<typeof useRequest>,
) {
  const documents = useDocumentList(request)

  return useProvideDocumentDataStore({
    data: computed(() => documents.documents),
    createDocument: async ({ purpose, room_id, files }) => Promise.all(
      files.map(document => documents.uploadDoc({ document, purpose, room_id })),
    ),
    updateDocument: async (documentRef, data) => documents.patchDoc(documentRef, data),
    deleteDocument: async documentRef => documents.deleteDoc(documentRef),
    getDocumentFileUrl: (documentRef, options) => {
      if (!request.requestRef || !documentRef)
        return ''

      const query = new URLSearchParams()
      if (options?.view)
        query.set('view', 'true')
      if (options?.variant)
        query.set('variant', options.variant)

      return utils.getDocumentUrl(
        request.requestRef,
        documentRef,
        query.toString(),
      )
    },
    isLoading: computed(() => documents.isLoading),
    isError: computed(() => documents.isError),
    error: computed(() => documents.error),
  })
}
